import { PlusOutlined } from "@ant-design/icons";
import { Button, Empty, Skeleton, Spin, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { lazy, Suspense, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ZoneOverlay } from "@/modules/zones/components/ZoneMap";
import { useCreateZone, useZonesList } from "@/modules/zones/hooks/useZones";
import type { Zone, ZoneCreateInput } from "@/modules/zones/types";
import { DataTable } from "@/shared/components/DataTable";
import { usePermission } from "@/shared/hooks/usePermission";

const { Text } = Typography;
const MAP_HEIGHT = 320;

const CHIZIQCHA = <span className="text-[#8a929e]">—</span>;

/**
 * ⚠️⚠️ **DINAMIK import — Leaflet + geoman + pmtiles +154 KB gzip**
 * (`routeTree.tsx` dagi xarita marshrutlari izohi, 1c spec §10.1).
 *
 * `lazyRouteComponent` bu yerda YORDAM BERMAYDI: `/departments/$id`
 * marshruti `routeTree.tsx` da STATIK import qilinadi (u xarita sahifasi
 * emas). Statik `import { ZoneMap }` butun xarita kutubxonasini asosiy
 * `index` bo'lagiga qo'shardi — shuning uchun bo'lak YORLIQ ICHIDA
 * ajratiladi (`employees/components/LateChartSlot.tsx` naqshi).
 *
 * ⚠️ Statik importga qaytarilsa `departments-bundle.test.ts` qizil bo'ladi —
 * u AYNAN shu qatorning shaklini pinlaydi. `ZoneOverlay` — faqat TIP
 * importi, build'da o'chiriladi.
 */
const ZoneMap = lazy(() =>
	import("@/modules/zones/components/ZoneMap").then((m) => ({ default: m.ZoneMap }))
);

/**
 * ⚠️ Hudud formasi ham DINAMIK — `ZoneFormModal` ichida tahrirlanadigan
 * `ZoneMap` (geoman) bor, ya'ni statik import yuqoridagi bilan AYNAN bir xil
 * sabab bilan Leaflet'ni `index` bo'lagiga qo'shardi
 * (`departments-bundle.test.ts` pinlaydi).
 */
const ZoneFormModal = lazy(() =>
	import("@/modules/zones/components/ZoneFormModal").then((m) => ({ default: m.ZoneFormModal }))
);

/**
 * Bo'lim profilining "Hududlar" tabi — haqiqiy `ZoneMap` + shu bo'limga
 * BEVOSITA bog'langan faol hududlar jadvali (`GET /zones?departmentId=`,
 * spec §8).
 *
 * ⚠️ UMUMIY (`departmentId = null`) hududlar bo'limi ATAYLAB YO'Q.
 * `ZoneListQuerySchema.departmentId` — `uuidSchema.optional()`, ya'ni
 * "faqat umumiy" filtri backend'da IFODALANMAYDI: `null` so'rovdan tushib
 * qolib, FILTRSIZ ro'yxatning 1-sahifasi keladi va undan mijozda
 * `departmentId === null` ni ajratish QISMAN (yolg'on) ro'yxat berardi.
 * Avval backend filtri kerak (reja 4, Task 6 briefi).
 *
 * ⚠️ `key={departmentId}` MAJBURIY (`DepartmentEmployeesTab` bilan bir xil
 * sabab): boshqa bo'limga o'tish AYNI marshrut, router komponentni qayta
 * mount QILMAYDI, `usePaginatedQuery` sahifasi esa ichki `useState` da —
 * kalitsiz yangi bo'lim oldingisining N-sahifasi bilan ochilardi.
 */
export function DepartmentZonesTab({
	departmentId,
	isActive = true,
}: {
	departmentId: string;
	/** Arxivlangan bo'limga yangi hudud biriktirilmaydi — «Hudud qo'shish» chizilmaydi. */
	isActive?: boolean;
}) {
	return (
		<DepartmentZonesContent key={departmentId} departmentId={departmentId} isActive={isActive} />
	);
}

function DepartmentZonesContent({
	departmentId,
	isActive,
}: {
	departmentId: string;
	isActive: boolean;
}) {
	const { t } = useTranslation("departments");
	const { can } = usePermission();
	const [formOpen, setFormOpen] = useState(false);
	const createZone = useCreateZone();
	const query = useZonesList({ isActive: true, departmentId });

	// «Hudud qo'shish» (14.09.2026): `ZoneFormModal` bo'lim QULFLANGAN holda.
	// Doira tekshiruvi backend'da (`assertDepartmentInScope`) — rahbar faqat
	// o'z bo'limlariga yarata oladi.
	const qoshish = can("zones", "create") && isActive;

	const handleSubmit = (input: ZoneCreateInput) => {
		// ⚠️ Bo'lim formadan EMAS, shu tabdan — maydon qulflangan bo'lsa ham
		// yuboriladigan qiymat bitta manbadan kelsin.
		createZone.mutate({ ...input, departmentId }, { onSuccess: () => setFormOpen(false) });
	};
	// ⚠️ `departmentId`GA BOG'LIQ EMAS — "Umumiy hududlar" (`department_id
	// IS NULL`) BARCHA bo'limlarga bir xil ko'rinadi (A2, spec §8). Faqat
	// o'qish uchun — amallar yo'q, chunki umumiy hududni FAQAT `global`
	// doira o'zgartira oladi (CLAUDE.md).
	const sharedQuery = useZonesList({ isActive: true, shared: true });

	// Xaritada joriy sahifadagi hududlar (`ZonesPage` bilan bir xil). `useMemo`
	// shart: `ZoneMap` overlay effekti massiv identifikatoriga bog'langan.
	const overlays = useMemo<ZoneOverlay[]>(
		() =>
			query.items
				.map((zone) => ({
					id: zone.id,
					name: zone.name,
					ring: zone.geometry.coordinates[0] ?? [],
				}))
				.filter((overlay) => overlay.ring.length > 0),
		[query.items]
	);

	const columns: ColumnsType<Zone> = [
		{ title: t("zonesTab.columns.name"), dataIndex: "name", key: "name" },
		{
			title: t("zonesTab.columns.type"),
			dataIndex: "tur",
			key: "tur",
			render: (tur: Zone["tur"]) => tur ?? CHIZIQCHA,
		},
	];

	return (
		<div>
			<Suspense fallback={<Skeleton.Node active style={{ width: "100%", height: MAP_HEIGHT }} />}>
				{/* ⚠️ `fitToOverlays` — bo'lim hududlari standart `DEFAULT_CENTER`
				    dan ekrandan tashqarida qolmasligi kerak (spec §8, Task 6). */}
				<ZoneMap overlays={overlays} height={MAP_HEIGHT} fitToOverlays />
			</Suspense>

			<div className="mt-6">
				<div className="flex flex-wrap items-center justify-between gap-2">
					<Text strong>{t("zonesTab.directTitle")}</Text>
					{qoshish ? (
						<Button type="primary" icon={<PlusOutlined />} onClick={() => setFormOpen(true)}>
							{t("zonesTab.addButton")}
						</Button>
					) : null}
				</div>
				{query.isLoading ? (
					<div className="mt-2">
						<Spin size="small" />
					</div>
				) : query.meta.total === 0 ? (
					<Empty
						className="mt-2"
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						description={t("zonesTab.empty")}
					/>
				) : (
					<div className="mt-2">
						<DataTable<Zone> columns={columns} query={query} />
					</div>
				)}
			</div>

			<div className="mt-6">
				<Text strong>{t("zonesTab.sharedTitle")}</Text>
				{sharedQuery.isLoading ? (
					<div className="mt-2">
						<Spin size="small" />
					</div>
				) : sharedQuery.meta.total === 0 ? (
					<Empty
						className="mt-2"
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						description={t("zonesTab.sharedEmpty")}
					/>
				) : (
					<div className="mt-2">
						<DataTable<Zone> columns={columns} query={sharedQuery} />
					</div>
				)}
			</div>

			{/* ⚠️ Faqat ochilganda mount qilinadi — forma xarita bo'lagini, bo'limlar
			    daraxti va mas'ul xodim qidiruvini yuklaydi; tabni shunchaki
			    ko'rayotgan foydalanuvchi uchun bu ortiqcha bo'lardi. */}
			{formOpen ? (
				<Suspense fallback={null}>
					<ZoneFormModal
						open
						editing={null}
						lockedDepartmentId={departmentId}
						isSubmitting={createZone.isPending}
						onCancel={() => setFormOpen(false)}
						onSubmit={handleSubmit}
					/>
				</Suspense>
			) : null}
		</div>
	);
}
